/**
 * AIDE-MÉMOIRE MEMBRES (public) — /rentree-membres : 5 actions à cocher, où regarder, règles,
 * protocole de communication, 5 minutes Impact, FAQ. Contenu réglé par le CEO (dates, actions, événements).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { RULES, PROTOCOL, SITUATIONS, FIVE_MINUTES, FAQ, GOLDEN_RULE } from "../components/rentree/rentreeContent";

const API = process.env.REACT_APP_BACKEND_URL;
const LS = "kaizen_rentree_actions";

const Section = ({ title, children, id }) => (
  <section id={id} className="mt-10">
    <h2 className="text-base md:text-lg font-black uppercase tracking-[0.25em] text-amber-400 mb-3">{title}</h2>
    {children}
  </section>
);

export default function RentreeAideMemoirePublic() {
  const [cfg, setCfg] = useState(null);
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(LS) || "[]"); } catch { return []; } });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saved, setSaved] = useState(null);
  const [tab, setTab] = useState("Élèves");

  useEffect(() => { axios.get(`${API}/api/public/rentree/aide-memoire`).then((r) => setCfg(r.data)).catch(() => setCfg({ actions: [], agenda: [] })); }, []);
  useEffect(() => { localStorage.setItem(LS, JSON.stringify(done)); }, [done]);

  const toggle = (i) => setDone((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i]));
  const confirm = async (e) => {
    e.preventDefault();
    try { const r = await axios.post(`${API}/api/public/rentree/confirm`, { email, name, actions_done: done }); setSaved(r.data); }
    catch { setSaved({ error: true }); }
  };
  if (!cfg) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center">Chargement…</div>;
  const total = (cfg.actions || []).length;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-5 py-10 max-w-3xl mx-auto" data-testid="rentree-public">
      <div className="text-amber-400 text-xs tracking-[0.35em] uppercase font-bold">Institut Impact Élite Martial · {cfg.season_label}</div>
      <h1 className="text-4xl sm:text-5xl font-black mt-1 leading-tight">Aide-mémoire de rentrée</h1>
      <p className="text-zinc-400 mt-2">Tout ce qu'il faut savoir pour bien commencer la session — parents, élèves, membres.</p>
      {(cfg.meeting_date || cfg.meeting_time) && <p className="text-amber-300 text-sm mt-2">📅 Rencontre : {[cfg.meeting_date, cfg.meeting_time].filter(Boolean).join(" · ")} · {cfg.meeting_place}</p>}

      <Section title={`Les ${total} actions obligatoires`} id="actions">
        {cfg.deadline && <p className="text-sm text-zinc-400 mb-3">À compléter avant le <b className="text-zinc-100">{cfg.deadline}</b>.</p>}
        <ul className="space-y-2" data-testid="actions-list">
          {(cfg.actions || []).map((a, i) => (
            <li key={i}>
              <button type="button" onClick={() => toggle(i)} data-testid={`action-${i}`}
                className={`w-full text-left flex gap-3 items-start rounded-xl border p-3.5 transition-colors ${done.includes(i) ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-600"}`}>
                <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center font-black text-sm ${done.includes(i) ? "bg-emerald-500 text-zinc-950" : "bg-amber-500 text-zinc-950"}`}>{done.includes(i) ? "✓" : i + 1}</span>
                <span className={done.includes(i) ? "text-zinc-400 line-through" : "text-zinc-100"}>{a}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-3 text-sm text-zinc-400" data-testid="actions-progress">{done.length}/{total} complétée{done.length > 1 ? "s" : ""}</div>
        <form onSubmit={confirm} className="mt-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-2" data-testid="confirm-form">
          <div className="text-sm font-bold text-zinc-100">Confirmer mes actions au dojo</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Courriel du dossier" className="rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm" data-testid="confirm-email" />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom de l'élève / famille" className="rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm" data-testid="confirm-name" />
          </div>
          <button type="submit" className="px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 font-bold text-sm" data-testid="confirm-submit">Envoyer ma confirmation</button>
          {saved && !saved.error && <div className="text-emerald-400 text-sm" data-testid="confirm-ok">✓ Merci ! {saved.done}/{saved.total} action{saved.done > 1 ? "s" : ""} confirmée{saved.done > 1 ? "s" : ""}{saved.all_done ? " — tout est complété 🥋" : ""}.</div>}
          {saved?.error && <div className="text-red-400 text-sm">Erreur — réessaie ou avise l'équipe.</div>}
        </form>
      </Section>

      <div className="mt-8 rounded-2xl bg-[#D90429] text-white p-5 font-bold text-lg">Règle d'or — {GOLDEN_RULE}</div>

      <Section title="Où regarder ou écrire ?" id="situations">
        <div className="rounded-xl border border-zinc-800 overflow-hidden">
          {SITUATIONS.map(([s, w]) => <div key={s} className="flex justify-between gap-3 px-4 py-2.5 border-b border-zinc-800/70 last:border-0 text-sm"><span className="text-zinc-300">{s}</span><span className="text-amber-300 text-right font-semibold">{w}</span></div>)}
        </div>
      </Section>

      <Section title="Le « 5 minutes Impact » — chaque semaine" id="routine">
        <ol className="grid sm:grid-cols-2 gap-2">{FIVE_MINUTES.map((f, i) => <li key={f} className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm"><span className="text-amber-400 font-black mr-2">{i + 1}</span>{f}</li>)}</ol>
      </Section>

      <Section title="Règles essentielles du dojo" id="regles">
        <div className="flex gap-2 mb-3">{Object.keys(RULES).map((k) => <button key={k} onClick={() => setTab(k)} data-testid={`rules-tab-${k}`} className={`px-3 py-1.5 rounded-full text-xs border ${tab === k ? "bg-amber-500 text-zinc-950 border-amber-500 font-bold" : "border-zinc-700 text-zinc-400"}`}>{k}</button>)}</div>
        <ul className="space-y-1.5">{RULES[tab].map((r) => <li key={r} className="text-sm text-zinc-200 flex gap-2"><span className="text-amber-400">›</span>{r}</li>)}</ul>
      </Section>

      <Section title="Protocole de communication" id="protocole">
        <div className="rounded-xl border border-zinc-800 overflow-hidden text-sm">
          {PROTOCOL.map(([s, c, d]) => <div key={s} className="px-4 py-2.5 border-b border-zinc-800/70 last:border-0"><div className="text-zinc-100 font-semibold">{s}</div><div className="text-zinc-400">{c} · <span className="text-amber-300">{d}</span></div></div>)}
        </div>
        <p className="text-xs text-zinc-500 italic mt-2">Un message Facebook, une discussion dans le stationnement ou une question lancée pendant un cours ne doit pas devenir le seul moyen de gérer une situation importante.</p>
      </Section>

      {cfg.events_text && <Section title="Événements et financement" id="evenements"><pre className="whitespace-pre-wrap font-sans text-sm text-zinc-200 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">{cfg.events_text}</pre></Section>}

      <Section title="Questions fréquentes" id="faq">
        <div className="space-y-2">{FAQ.map(([q, a]) => <details key={q} className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-2.5"><summary className="text-sm font-semibold text-zinc-100 cursor-pointer">{q}</summary><p className="text-sm text-zinc-400 mt-1">{a}</p></details>)}</div>
      </Section>

      <p className="mt-12 text-zinc-500 text-sm">Prochain grand bilan : {cfg.next_meeting_label}. Méthode Kaizen — un degré chaque jour. 🥋</p>
    </div>
  );
}
